/**
 * AB#1754 — WCAG 2.1 contrast ratio computation.
 *
 * Mirrors the BE `IColorContrastValidator` math so the FE warning chip
 * matches BE telemetry: server logs `low_contrast_warning` when ratio <
 * 4.5, but persists either way (owner override is allowed by design —
 * brand-fidelity beats strict AA). This util powers the inline ⚠ hint
 * on the Calendar Settings status table.
 *
 * Reference: https://www.w3.org/TR/WCAG21/#contrast-minimum
 */

/** WCAG AA threshold for normal text (≥ 18pt / 14pt bold uses AA Large = 3.0). */
export const WCAG_AA_NORMAL_TEXT = 4.5;

/**
 * Dark-theme calendar background used for the contrast check. Matches the
 * Devanza dark-theme token used by the appointment card chips.
 */
export const DEVANZA_DARK_BG = '#0b0b0f';

/**
 * Parse `#RRGGBB` / `#RGB` into a 0..255 tuple. Returns null on malformed
 * input — caller treats that as "no warning" (the colour picker constrains
 * to valid hex anyway).
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    if (!hex || typeof hex !== 'string') return null;
    const trimmed = hex.trim().replace(/^#/, '');
    let r: number;
    let g: number;
    let b: number;
    if (trimmed.length === 3) {
        r = parseInt(trimmed[0] + trimmed[0], 16);
        g = parseInt(trimmed[1] + trimmed[1], 16);
        b = parseInt(trimmed[2] + trimmed[2], 16);
    } else if (trimmed.length === 6) {
        r = parseInt(trimmed.slice(0, 2), 16);
        g = parseInt(trimmed.slice(2, 4), 16);
        b = parseInt(trimmed.slice(4, 6), 16);
    } else {
        return null;
    }
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return { r, g, b };
}

/**
 * Relative luminance per WCAG 2.1 §1.4.3. Inputs are 0..255 sRGB channels.
 *
 * L = 0.2126*R + 0.7152*G + 0.0722*B   (R/G/B after gamma decode)
 */
function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
    const channel = (c: number): number => {
        const cs = c / 255;
        return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/**
 * WCAG 2.1 contrast ratio between two hex colours. Returns a number in
 * the range [1.0, 21.0]. Returns 1.0 (worst) when either hex is malformed
 * — the caller treats that as no-warning so the picker doesn't flicker
 * red on every keystroke while a hex is being typed.
 */
export function wcagContrastRatio(fgHex: string, bgHex: string): number {
    const fg = hexToRgb(fgHex);
    const bg = hexToRgb(bgHex);
    if (!fg || !bg) return 1;
    const lFg = relativeLuminance(fg);
    const lBg = relativeLuminance(bg);
    const lighter = Math.max(lFg, lBg);
    const darker = Math.min(lFg, lBg);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Pass / fail against AA Normal (4.5:1). */
export function meetsAa(ratio: number): boolean {
    return ratio >= WCAG_AA_NORMAL_TEXT;
}

/**
 * Convenience: returns true when the given colour has sufficient contrast
 * against the Devanza dark-theme background to qualify as AA-readable.
 * Used by the Calendar Settings UI to show / hide the ⚠ warning chip.
 */
export function isReadableOnDarkBg(fgHex: string): boolean {
    return meetsAa(wcagContrastRatio(fgHex, DEVANZA_DARK_BG));
}
